import { useMediaQuery } from '@mui/material';
import { createContext, ReactNode, useContext } from 'react';
import {
  MotionDurations,
  MOTION_DURATIONS,
} from '../../constants/motion.constant';

export interface MotionSettings {
  durations: MotionDurations;
  /** OS-level "reduce motion". Primitives drop transforms and keep opacity. */
  prefersReducedMotion: boolean;
}

const MotionContext = createContext<MotionSettings>({
  durations: MOTION_DURATIONS,
  prefersReducedMotion: false,
});

interface MotionProviderProps {
  durations?: MotionDurations;
  children: ReactNode;
}

/**
 * Supplies the duration scale and the reduced-motion preference to every
 * primitive under it. Mounted once at the module root; tests mount it with
 * `ZERO_MOTION_DURATIONS` so nothing waits on an animation frame.
 */
export const MotionProvider = ({
  durations = MOTION_DURATIONS,
  children,
}: MotionProviderProps): JSX.Element => {
  const prefersReducedMotion = useMediaQuery(
    '(prefers-reduced-motion: reduce)',
  );

  return (
    <MotionContext.Provider value={{ durations, prefersReducedMotion }}>
      {children}
    </MotionContext.Provider>
  );
};

export const useMotion = (): MotionSettings => useContext(MotionContext);

/** Seconds, as framer-motion expects. */
export const toSeconds = (ms: number): number => ms / 1000;
